import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  advanceProject,
  approveCrp,
  AttestationInvalidError,
  attestationOf,
  briefItemName,
  clearVariance,
  releaseCarryOf,
  CrpConflictError,
  CrpForbiddenError,
  getBasis,
  getCrpState,
  lockBasis,
  BasisLockError,
  getProject,
  getProjectDecisions,
  getProjectDisputes,
  getProjectDocuments,
  attestFinding,
  getProjectFacts,
  setProjectYieldFloor,
  getProjectRetrievals,
  getProjectVariances,
  RECORD_STATES,
  updateProjectSponsor,
  type AdvanceOffer,
  type BasisState,
  type CrpBrief,
  type CrpState,
  type DecisionRecord,
  type DisputeLedger,
  type ProjectDetail,
  type ProjectDocument,
  type ProjectRetrievals,
  type FactResolutionSummary,
  type OpenItemsSummaryView,
  type ReviewCoverageReadView,
  answerGateQuestion,
  type GateAnswer,
  type GateFinding,
  type RecordFactView,
  type RecordState,
  type SessionClaims,
  type Variance,
  type VariancePosture,
} from '../api.js';
import { useAppSurface, useChrome } from '../chrome.js';
import { AnalyzerLink } from '../components/AnalyzerLink.js';
import { designReviewReport } from '../design-review-report.js';
import { DesignReviewReport, hasDesignReview } from '../components/DesignReviewReport.js';
import {
  deciderLabel,
  DisputeAction,
  DisputeLedgerCard,
  type DisputesContext,
} from '../components/DecisionDisputes.js';
import { ProjectAsk } from '../components/ProjectAsk.js';
import { ProjectDocuments, unlistedJudgedCount } from '../components/ProjectDocuments.js';
import { DrawingSetAsFiled, ProjectDrawings } from '../components/ProjectDrawings.js';
import { RecordFacts } from '../components/RecordFacts.js';
import { YieldFloor } from '../components/YieldFloor.js';
import { OpenItemsRegister } from '../components/OpenItemsRegister.js';
import { TrackedQuantities } from '../components/TrackedQuantities.js';
import { ReportSections, buildSections } from '../components/ReportSections.js';
import { RetrievalLog } from '../components/RetrievalLog.js';
import { usePrintExpanded } from '../components/usePrintExpanded.js';
import { useSession } from '../useSession.js';
import {
  canAnswerDispute,
  canAskRecord,
  canAttestStop,
  canDisputeDecision,
  canLockBasis,
  canRetrieveDocument,
  canRequestReport,
  canReunderwrite,
  canRunReview,
  userIdOf,
} from '../roles.js';
import { RecordReviews } from '../components/RecordReviews.js';
import { RecordChanges } from '../components/RecordChanges.js';
import { percentFromRatio, timestamp } from '../format.js';
import { VersionDeltasPanel } from '../components/VersionDeltasPanel.js';

/** Roles permitted to release spend or attest a phase — mirrors the api's `crp:approve` (server enforces the 403 too). */
const APPROVER_ROLES = new Set(['owner', 'admin']);

/**
 * Roles permitted to ingest documents into a record — mirrors the api's INTAKE_ROLES. Ingesting is an
 * employee act (canon R1), so a viewer is read-only by name. Broader than APPROVER_ROLES on purpose: a
 * `member` may add a document to the record but may not release a CRP against it.
 */
const INTAKE_ROLES = new Set(['owner', 'admin', 'member']);

/** Read the caller's membership role off the permissive session claims. */
function roleOf(claims: SessionClaims): string | null {
  const role = claims['role'];
  return typeof role === 'string' ? role : null;
}

/** Title-case a record-state slug, e.g. "underwritten" → "Underwritten". */
function stateLabel(state: RecordState): string {
  return state.charAt(0).toUpperCase() + state.slice(1);
}

type Load =
  | { status: 'loading' }
  | { status: 'notfound'; code: string }
  | { status: 'error'; code: string; message: string }
  | {
      status: 'ready';
      /** The route handle this state was loaded FOR — a refresh may only keep a record it re-reads. */
      code: string;
      project: ProjectDetail;
      decisions: DecisionRecord[];
      /** Disputes of those decisions (ADR-0076). Null = the ledger could not be read, NOT "none". */
      disputes: DisputeLedger | null;
      crp: CrpState | null;
      basis: BasisState | null;
      variances: Variance[];
      /** The severest OPEN rung + whether anyone is on it (canon GT §4.4). */
      posture: VariancePosture;
      documents: ProjectDocument[];
      /** What has LEFT the record — the handovers ledger (canon R4). */
      retrievals: ProjectRetrievals;
      /** What the record CLAIMS, and how strongly — provisional-then-confirmed (ADR-0044). */
      facts: RecordFactView[];
      /** The server-side owed/overdue/unresolved summary (Phase 3a). Null = no answer, not a clean one. */
      factResolution: FactResolutionSummary | null;
      openItems: OpenItemsSummaryView | null;
      /** Which protected questions the technical review asked (#1379). Null = not said. */
      reviewCoverage: ReviewCoverageReadView | null;
      findingsWithheld: number | null;
      /**
       * Set when a REFRESH of an already-loaded record failed. The page keeps what it had and says the
       * view may be out of date, instead of replacing everything with the error state.
       */
      refreshError?: string;
    };

/** True when an unresolved Stop&Fix exists — the record is hard-blocked from advancing (D6). */
function hasOpenStopFix(variances: Variance[]): boolean {
  return variances.some((v) => v.level === 'stop_fix' && v.status === 'open');
}

export function ProjectView() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const session = useSession();
  // Supplied by the application root — `AppHeader` in the Deal Analyzer, `HubHeader` in the Hub.
  // This page is rendered by both and no longer decides which product it belongs to (see chrome.tsx).
  const Chrome = useChrome();
  // Which application this page is on — Hub-only features read it (see chrome.tsx).
  const surface = useAppSurface();
  const [loaded, setLoad] = useState<Load>({ status: 'loading' });
  /*
    🔴 A RECORD LOADED FOR ANOTHER ADDRESS READS AS LOADING (Bugbot on #1403 and #1405). This page
    stays mounted across `/projects/:code` changes, and the reset effect below only runs after the
    first render with the new code — so for that render, project A's data would sit under B's
    address with B's actions. Deriving it here closes that frame without remounting the page (a
    remount also re-fetched the session on every project switch, which Bugbot flagged on #1405).
  */
  const load: Load =
    loaded.status !== 'loading' && loaded.code !== code ? { status: 'loading' } : loaded;
  // The record this page is addressed to NOW. A load that settles after the reader moved on to
  // another project must not write its record into the page (see the `code` guard below).
  const currentCode = useRef(code);
  // Set on COMMIT, not during render: an abandoned transition render must not move it.
  useLayoutEffect(() => {
    currentCode.current = code;
  }, [code]);

  const reload = useCallback(async () => {
    if (!code) return;
    try {
      const project = await getProject(code);
      if (currentCode.current !== code) return;
      if (!project) {
        setLoad({ status: 'notfound', code });
        return;
      }
      // Decisions + CRP + variances are non-fatal (each degrades to empty/null) — the header always renders.
      const [decisions, disputes, crp, basis, variances, documents, retrievals, facts] =
        await Promise.all([
          getProjectDecisions(code),
          getProjectDisputes(code),
          getCrpState(code),
          getBasis(code),
          getProjectVariances(code),
          getProjectDocuments(code),
          getProjectRetrievals(code),
          getProjectFacts(code),
        ]);
      if (currentCode.current !== code) return;
      setLoad({
        status: 'ready',
        code,
        project,
        decisions,
        disputes,
        crp,
        basis,
        variances: variances.variances,
        posture: variances.posture,
        documents,
        retrievals,
        facts: facts.facts,
        factResolution: facts.resolution,
        openItems: facts.openItems,
        // `?? null`: an older response, or a test double, that does not carry it says nothing.
        reviewCoverage: facts.reviewCoverage ?? null,
        findingsWithheld: facts.findingsWithheld ?? null,
      });
    } catch (err) {
      if (currentCode.current !== code) return;
      const message = err instanceof Error ? err.message : 'Unexpected error.';
      /*
        ⚠️ A FAILED REFRESH MUST NOT UNMOUNT A LOADED PAGE (Bugbot, #1403). `reload` is also what a
        finished run follower and an upload call to bring the record up to date — and switching to the
        error state unmounts `ProjectDocuments`, taking the "re-underwrite finished" notice and its link
        to the result with it, over one blip. Only a FIRST load has nothing to keep.
      */
      /*
        ⚠️ ONLY THE SAME RECORD (Bugbot, #1403 at d8c5ec16). This page stays mounted across
        `/projects/:code` changes, so a `ready` state may belong to the PREVIOUS project. Keeping it on a
        failed load of the next one would show project A under project B's address while every action
        on the page writes to B.
      */
      setLoad((prev) =>
        prev.status === 'ready' && prev.code === code
          ? { ...prev, refreshError: message }
          : { status: 'error', code, message },
      );
    }
  }, [code]);

  useEffect(() => {
    if (session.status !== 'authed') return;
    void reload();
  }, [session.status, reload]);

  if (session.status !== 'authed') {
    return <div className="app__loading">Loading…</div>;
  }

  const role = roleOf(session.claims) ?? '';
  const canApprove = APPROVER_ROLES.has(role);
  const canIngest = INTAKE_ROLES.has(role);
  // One definition, in `roles.ts` — the document find surface reads the same one.
  const canRetrieve = canRetrieveDocument(session.claims);
  // Staff role AND the Hub — Brad ruled 2026-09-10 that the answerer lives on the Hub only (ADR-0045
  // §4). `app.*` is the surface a client will look at. A root that forgets its surface hides it.
  const canAsk = canAskRecord(session.claims) && surface === 'hub';
  // Owner only, by ruling (ADR-0063 Q2) — the server refuses anyone else with a 403.
  const canAttest = canAttestStop(session.claims);
  const canLock = canLockBasis(session.claims);
  // A staff act that spends model budget — Hub only, like the answerer.
  const canReview = canRunReview(session.claims) && surface === 'hub';
  // A paid underwrite a PERSON starts — staff and Hub only, on the review's reasoning.
  const canRerun = canReunderwrite(session.claims) && surface === 'hub';
  // Asking Nexus to render the deal report — a staff act, Hub only, beside the re-underwrite.
  const canReport = canRequestReport(session.claims) && surface === 'hub';
  // Disputing spends nothing and is offered wherever the timeline is shown, by role alone: staff
  // file, owner/admin answer, a viewer (a client) reads the ledger (ADR-0076 Q1).
  const canDispute = canDisputeDecision(session.claims);
  const canAnswerDisputes = canAnswerDispute(session.claims);
  const userId = userIdOf(session.claims);
  /** How this page was addressed — a project number, or a pursuit's record id. See the note below. */
  const ref = code ?? '';

  return (
    <div className="app">
      {Chrome !== null && <Chrome claims={session.claims} />}
      <main className="app__main">
        <div className="app__crumbs">
          <button type="button" className="link-btn" onClick={() => void navigate('/projects')}>
            ← All projects
          </button>
        </div>

        {load.status === 'loading' && <div className="app__loading">Loading the project…</div>}

        {load.status === 'notfound' && (
          <div className="notice">
            <strong>Project not found.</strong> It doesn&rsquo;t exist in your workspace, or you
            don&rsquo;t have access to it.
          </div>
        )}

        {load.status === 'error' && (
          <div className="notice error">
            <strong>Couldn&rsquo;t load the project.</strong> {load.message}
          </div>
        )}

        {load.status === 'ready' && (
          <>
            {/*
              ⚠️ THE ROUTE PARAM, NOT `project.code` — canon R5, "no second-class records".

              A PURSUIT CARRIES NO PROJECT NUMBER (R-e), so `project.code` is null for one and every
              child below would have called `/projects/null/...`. The handle that actually resolves is
              whatever this page was addressed BY: a number for a numbered project, the record's id for
              a pursuit. Passing the code was correct only for as long as pursuits could not be created,
              which stopped being true the moment the intake form shipped.
            */}
            {/*
              ⚠️ THE HEADER AND THE LADDER STAY OUTSIDE THE TABS, deliberately. They answer "what am I
              looking at and where has it got to" — the two questions a reader needs no matter which
              panel they are in, and the two that make a tab bar comprehensible in the first place.
              Putting them behind a tab would mean the identity of the record could be off-screen while
              you read its variances.
            */}
            {load.refreshError && (
              <div className="notice warn" role="status">
                <strong>This view may be out of date.</strong> Refreshing the record failed (
                {load.refreshError}). Reload the page to try again.
              </div>
            )}
            <ProjectHeader
              project={load.project}
              onFileCount={load.documents.length}
              unlistedCount={unlistedJudgedCount(load.project.documentCount, load.documents)}
            />
            <RecordChanges
              previous={load.project.previousUnderwrite}
              latest={load.project.latestUnderwrite}
              latestAnalysisId={load.project.latestAnalysisId ?? null}
            />
            <StateLadder current={load.project.recordState} />
            <ProjectTabs
              code={ref}
              load={load}
              canIngest={canIngest}
              canRetrieve={canRetrieve}
              canAsk={canAsk}
              canApprove={canApprove}
              canAttest={canAttest}
              canLock={canLock}
              canReview={canReview}
              canRerun={canRerun}
              canReport={canReport}
              canDispute={canDispute}
              canAnswerDisputes={canAnswerDisputes}
              userId={userId}
              reload={reload}
            />
          </>
        )}
      </main>
    </div>
  );
}

/**
 * "See this project's deals" — a link, not a button, so it can be middle-clicked and copied like any
 * other address. Its own component because `ProjectHeader` takes only the record and this needs the
 * router; keeping it separate leaves the header a pure presentation of the project.
 *
 * 🔴 IT USED TO BE A BUTTON THAT NAVIGATED IN-APP, AND ON THE HUB THAT WENT NOWHERE (#1393). The Hub
 * has no `/analyses` route; its catch-all redirected the click to `/projects`, so the link "worked" by
 * dropping the reader on the project list. The deals list lives on the analyzer, so from the Hub this
 * is now a cross-origin link there — and, as the sentence above always said, an actual link.
 */
function ProjectDealsLink({ code }: { code: string }) {
  return (
    <AnalyzerLink
      className="link-btn proj__deals"
      to={`/analyses?project=${encodeURIComponent(code)}`}
    >
      See this project&rsquo;s deals →
    </AnalyzerLink>
  );
}

/**
 * The living SPONSOR PROFILE (#501) — the one place a project's sponsor evidence is captured.
 *
 * ⚠️ WHY IT IS HERE AND NOT ON THE UPLOAD FORM. #502 put sponsor evidence on the SUBMISSION, which
 * fixes the manual path and cannot fix the autonomous one: a connector-synced document drop
 * re-underwrites this project with nobody at the keyboard, so it can only inherit what the RECORD
 * holds. Typed here once, every future run of this project uses it — including runs no human starts.
 *
 * The hint says what the field DOES, because a field whose effect is invisible does not get filled in
 * (canon R8/R10). "20 of the 100 points" is the honest stake.
 */
export function SponsorProfile({
  code,
  sponsorName,
  sponsorContext,
  canEdit,
  onSaved,
}: {
  code: string;
  sponsorName: string | null;
  sponsorContext: string | null;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(sponsorName ?? '');
  const [context, setContext] = useState(sponsorContext ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Re-seed when the record reloads — otherwise a save elsewhere leaves this form showing stale text.
  //
  // ⚠️ DO NOT CLEAR `saved` HERE. A successful save reloads the record, which changes these props,
  // which runs this effect — so clearing `saved` here made the confirmation unreachable in every save
  // sequence: it was still `dirty` when `saved` was set, and no longer `saved` once `dirty` cleared.
  // The user got no acknowledgement, and lost the one sentence that says the profile applies to the
  // NEXT underwrite rather than to reports already run. Editing withdraws it instead — the render
  // condition below is `saved && !dirty`. Pinned by `SponsorProfile.interaction.test.tsx`.
  useEffect(() => {
    setName(sponsorName ?? '');
    setContext(sponsorContext ?? '');
    setError(null);
  }, [sponsorName, sponsorContext]);

  const dirty = name !== (sponsorName ?? '') || context !== (sponsorContext ?? '');
  const captured = (sponsorContext ?? '').trim() !== '';

  async function onSave() {
    setBusy(true);
    setError(null);
    try {
      await updateProjectSponsor(code, { sponsorName: name, sponsorContext: context });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the sponsor profile.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3 className="card__title">Sponsor &amp; development team</h3>
      <p className="card__prose">
        {captured
          ? 'Every underwrite of this project scores Sponsor & Execution against the evidence below — including runs started automatically when documents arrive.'
          : 'No sponsor profile has been captured. Sponsor & Execution is scored with no sponsor information and reported as unevidenced — 20 of the 100 points.'}
      </p>

      <label className="df__field df__field--full">
        <span className="df__label">Sponsor / borrower</span>
        <span className="df__inputwrap">
          <input
            type="text"
            placeholder="e.g. Ridgeline Development LLC"
            value={name}
            disabled={!canEdit || busy}
            onChange={(e) => setName(e.target.value)}
          />
        </span>
        <span className="df__hint">
          Used to look up public SEC filings for this project. Leave blank to skip.
        </span>
      </label>

      <label className="df__field df__field--full">
        <span className="df__label">Track record &amp; principals</span>
        <textarea
          rows={4}
          placeholder="What have they built? e.g. Ridgeline has delivered 9 multifamily projects since 2011 (2,400 units, Raleigh–Durham). Principals: J. Marek (28 yrs), A. Solano (16 yrs). GC: Hoffman Construction — 6 prior projects together."
          value={context}
          disabled={!canEdit || busy}
          onChange={(e) => setContext(e.target.value)}
        />
        <span className="df__hint">
          Scored — this is the evidence behind Sponsor &amp; Execution, 20 of the 100 points. It is
          never read from your documents, so it has to be stated here.
        </span>
      </label>

      {error && <div className="notice error">{error}</div>}
      {saved && !dirty && (
        <div className="notice">
          Saved. It applies to the next underwrite of this project — existing reports keep the score
          they were run with.
        </div>
      )}

      {canEdit ? (
        <button type="button" onClick={() => void onSave()} disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save sponsor profile'}
        </button>
      ) : (
        <p className="muted">Your role can view this project but not edit its sponsor profile.</p>
      )}
    </section>
  );
}

/**
 * The header's document count, in words.
 *
 * 🔴 THE NUMBER IS WHAT AN UNDERWRITE JUDGES, and it used to be something else. The header said "417
 * documents" (raw `document_ids`, withdrawals included), the Documents tab listed 727, the re-underwrite
 * confirm said 662, and the run judged 373 — four numbers for one record (The Commons, 2025-014). The
 * judged count is the server's (`judgedDocumentCount`, the worker's own rule).
 *
 * When the Documents tab lists MORE than that — withdrawn files, and files no longer on the record — it
 * says so, rather than leaving a reader to reconcile two numbers that disagree without explanation.
 */
export function documentCountLabel(
  judged: number | undefined,
  onFile: number,
  /** Judged documents with no intake stamp, which the Documents tab cannot list. */
  unlisted = 0,
): string {
  // Absent is not zero: an api that predates the count states only what the list holds.
  if (judged === undefined) return `${String(onFile)} document${onFile === 1 ? '' : 's'} on file`;
  /*
    ⚠️ "· 727 on file" READS AS "the 373 are among the 727", and that is only true when every judged
    document is listed. When some carry no intake stamp they are judged but NOT on file, so the head
    says how many — otherwise the two numbers imply a subset that does not hold.
  */
  const head =
    `${String(judged)} document${judged === 1 ? '' : 's'} judged` +
    (unlisted > 0 ? ` (${String(unlisted)} not listed)` : '');
  return onFile > judged - unlisted ? `${head} · ${String(onFile)} on file` : head;
}

/** The living-record header: name/code, record-state chip, latest verdict, doc count, currency. */
export function ProjectHeader({
  project,
  onFileCount,
  unlistedCount = 0,
}: {
  project: ProjectDetail;
  /** How many documents the Documents tab lists — one row per document stamped, judged or not. */
  onFileCount: number;
  /** Judged documents with no intake stamp — the same number the Documents panel states. */
  unlistedCount?: number;
}) {
  const u = project.latestUnderwrite;

  return (
    <header className="proj__head">
      <div className="proj__id">
        <h1 className="proj__name">{project.name ?? project.code ?? 'Untitled record'}</h1>
        {/*
          ⚠️ A PURSUIT HAS NO NUMBER AND MUST NOT LOOK BROKEN FOR IT (canon R5 — no second-class
          records; R-e — numbers are minted at promotion, never before). It says what it IS, where a
          numbered project says which number it is. The record's uuid is deliberately NOT shown: it is
          an address, not a name, and putting it here would read as the identifier canon D1 has
          expressly reserved to a founder ruling.
        */}
        <span className="proj__code">
          {project.code ?? 'Pursuit — number assigned at promotion'}
        </span>
      </div>
      <div className="proj__meta">
        <span className={`statechip statechip--${project.recordState}`}>
          {stateLabel(project.recordState)}
        </span>
        {u && (u.verdict || typeof u.overallScore === 'number') && (
          <span className={`verdictchip verdictchip--${(u.verdict ?? 'na').toLowerCase()}`}>
            {u.verdict ?? '—'}
            {typeof u.overallScore === 'number' && <strong> {u.overallScore}</strong>}
          </span>
        )}
        <span className="muted">
          {documentCountLabel(project.judgedDocumentCount, onFileCount, unlistedCount)} · updated{' '}
          {timestamp(project.updatedAt)}
        </span>
        <CurrencyBadge project={project} />
        {/* The route from a project record to the underwrites that produced it. This page showed a
            project's latest verdict but offered no way to reach the run behind it, or any run before
            it — the record was a dead end.

            ⚠️ NUMBERED RECORDS ONLY, and the omission is honest rather than tidy. The analyses list
            filters on the run's project CODE (`runProjectCode`, either stored shape), so a pursuit — which has no code —
            would render a link that returns an EMPTY list every time. A silently empty answer is worse
            than an absent link: it says "this record has no deals" when what happened is that the
            filter cannot express the question. Making a pursuit's runs findable means teaching that
            filter about record ids, which is a change to how runs are correlated and not this one. */}
        {project.code !== null && <ProjectDealsLink code={project.code} />}
      </div>
    </header>
  );
}

/**
 * R8's CURRENCY BADGE — "every artifact visibly declares its currency", which ADR-0035 quotes as the
 * product's core promise: _"the never-work-from-a-superseded-drawing guarantee is the product's core
 * promise, DISPLAYED, not implied."_
 *
 * ⚠️ #653 IS WHY THIS EXISTS. The Commons underwrote from a 2026-07-20 proforma for twelve runs while
 * its 2026-08-07 replacement sat unread in the same document set. Every figure on the page was
 * internally consistent, and every one was read from a superseded file. No surface said how old the
 * underwrite was, or that the documents had moved past it — so a reader could not have asked the
 * question, never mind answered it.
 *
 * Three states, and each says something different:
 *   never underwritten  — a real state, not a zero date. The record exists; no run has read it.
 *   current             — underwritten, and no document has landed since. The quiet, common case.
 *   STALE               — N documents arrived after the last run began, or M documents the run
 *                         could have read have a newer version since. THE ONE THAT MATTERS. Added
 *                         and revised are separate counts and separate words: a revision is not a
 *                         new document, and a re-saved file is not two.
 *   STALE (withdrawn)   — N documents were WITHDRAWN after the last run began, so its figures still
 *                         include them; and when that leaves nothing to judge, it says so instead.
 *
 * ⚠️ It states the fact and stops. It does not say "re-run this" — whether a new document should move
 * a governed number is a person's call (R7: a conclusion that binds a person is a recommendation
 * carrying a Human Decision Point, never a verdict), and a badge that nagged would be ignored within a
 * week, which is Chad's own documented failure mode for tools that add friction without returning
 * value.
 */
export function CurrencyBadge({
  project,
}: {
  project: Pick<
    ProjectDetail,
    | 'lastUnderwrittenAt'
    | 'documentsAddedSince'
    | 'documentsRevisedSince'
    | 'documentsWithdrawnSince'
    | 'documentsRestoredSince'
    | 'judgedDocumentCount'
  >;
}) {
  if (!project.lastUnderwrittenAt) {
    return <span className="currency currency--none">Not yet underwritten</span>;
  }
  const n = project.documentsAddedSince;
  /*
    REVISED SINCE — a newer version of a document the run could have read (a connector re-save, a
    version upload). Until the API split it out, these were counted as "added", and two re-saves of one
    file as two.

    ⚠️ Absent ⇒ 0 for the WORDING only: an api that predates the split counted every stamp in
    `documentsAddedSince`, revisions included, so its `n === 0` already covers them and "no documents
    since" stays true. Absent `n` still makes no claim (below).
  */
  const v = typeof project.documentsRevisedSince === 'number' ? project.documentsRevisedSince : 0;
  const moved = movedSincePrefix(n, v);
  /*
    WITHDRAWN SINCE — the other way the document set moves under a run. A withdrawal takes a document
    out of what the NEXT run judges, but the figures on this page were computed with it in.

    ⚠️ Absent is not zero here either: an api that predates the count says nothing about withdrawals,
    so `w` is only ever read when it is a number, and an absent `w` leaves every sentence below as it
    was.

    ⚠️ It states the fact and what a re-underwrite would do; it does not tell anyone to run one (the
    R7 note above). When NOTHING is left to judge, a re-underwrite is refused (409 `nothing-judged`), so
    pointing at one would send a reader to a button that cannot work — that case says so instead.
  */
  const w =
    typeof project.documentsWithdrawnSince === 'number' ? project.documentsWithdrawnSince : 0;
  if (w > 0 && project.judgedDocumentCount === 0) {
    return (
      <span className="currency currency--stale">
        ⚠ Every document behind the underwrite of {timestamp(project.lastUnderwrittenAt)} has been
        withdrawn — nothing remains on the record to judge
      </span>
    );
  }
  /*
    RESTORED SINCE (migration 0066) — the mirror: a document withdrawn when the run began and judged
    again now. A restore adds no stamp, so `documentsAddedSince` cannot see it; without this the badge
    fell through to "no documents since" over a set the next run judges differently. Absent ⇒ 0, as
    `w`: an api that predates the count says nothing about restores.
  */
  const r = typeof project.documentsRestoredSince === 'number' ? project.documentsRestoredSince : 0;
  const restoredPart = r > 0 ? `${String(r)} document${r === 1 ? '' : 's'} restored and ` : '';
  if (w > 0) {
    return (
      <span className="currency currency--stale">
        ⚠ {moved}
        {restoredPart}
        {w} document{w === 1 ? '' : 's'} withdrawn since this was underwritten{' '}
        {timestamp(project.lastUnderwrittenAt)} — its figures still include{' '}
        {w === 1 ? 'it' : 'them'}; a re-underwrite would judge without {w === 1 ? 'it' : 'them'}
      </span>
    );
  }
  /*
    ⚠️ ABSENT IS NOT ZERO. `?? 0` here would render "no documents since" — an assertion of currency —
    for a response that never carried the count at all (an older cached payload, a client running ahead
    of the server). That is [[a-disclosure-without-its-report-asserts-a-clean]]: the reader sees a clean
    bill of health produced by the absence of the check rather than by the check passing.

    So an unknown count states only the fact we hold — WHEN it was underwritten — and makes no claim
    about what has happened since.
  */
  if (r > 0) {
    return (
      <span className="currency currency--stale">
        ⚠ {moved}
        {r} document{r === 1 ? '' : 's'} restored since this was underwritten{' '}
        {timestamp(project.lastUnderwrittenAt)} — its figures do not include{' '}
        {r === 1 ? 'it' : 'them'}; a re-underwrite would judge {r === 1 ? 'it' : 'them'}
      </span>
    );
  }
  if (moved !== '') {
    // `moved` ends "… and " for the sentences above; here it is the whole subject.
    return (
      <span className="currency currency--stale">
        ⚠ {moved.slice(0, -' and '.length)} since this was underwritten{' '}
        {timestamp(project.lastUnderwrittenAt)}
      </span>
    );
  }
  if (n === undefined || n === null) {
    return <span className="currency">Underwritten {timestamp(project.lastUnderwrittenAt)}</span>;
  }
  return (
    <span className="currency currency--current">
      Underwritten {timestamp(project.lastUnderwrittenAt)} · no documents since
    </span>
  );
}

/**
 * "N documents added and M documents revised and " — the documents that moved INTO what the next run
 * reads, as the head of a sentence (empty when neither moved). Only a positive NUMBER speaks: an
 * absent count is never coerced into a claim.
 */
function movedSincePrefix(added: number | null | undefined, revised: number): string {
  const part = (k: number, verb: string): string =>
    `${String(k)} document${k === 1 ? '' : 's'} ${verb} and `;
  return (
    (typeof added === 'number' && added > 0 ? part(added, 'added') : '') +
    (revised > 0 ? part(revised, 'revised') : '')
  );
}

/** The record-state ladder — where the LPR sits on the Set-B lifecycle (passed / current / ahead). */
function StateLadder({ current }: { current: RecordState }) {
  const idx = RECORD_STATES.indexOf(current);
  return (
    <section className="card ladder">
      <h3 className="card__title">Record state</h3>
      <ol className="ladder__track" aria-label="Project lifecycle state">
        {RECORD_STATES.map((s, i) => {
          const rung = i < idx ? 'done' : i === idx ? 'current' : 'ahead';
          return (
            <li key={s} className={`rung rung--${rung}`}>
              <span className="rung__dot" aria-hidden="true">
                {/* No number: this line's position is not canon's phase number (ADR-0086). */}
                {rung === 'done' ? '✓' : ''}
              </span>
              <span className="rung__label">{stateLabel(s)}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * A Conditional Release Point, decided from its brief (ADR-0070 D1/D2). A release APPROVES SPEND on the
 * next phase — it never moves the record's state; the "Advance phase" panel does that. The api enforces
 * the role (403), the placement and the once-only rule (409) regardless of what this panel shows.
 */
export function ReleasePanel({
  code,
  brief,
  requires,
  prerequisiteMet,
  lockedBasisMet,
  canApprove,
  blocked,
  onDone,
}: {
  code: string;
  brief: CrpBrief;
  /** The release this one needs first, and whether it has been made (CRP-2 needs CRP-1). */
  requires: CrpBrief['crp'] | null;
  prerequisiteMet: boolean;
  /** CRP-3 needs a locked basis first (ADR-0071 D5). */
  lockedBasisMet: boolean;
  canApprove: boolean;
  /** An open Stop&Fix variance blocks a release (ADR-0070 D6) — the api enforces the 409 too. */
  blocked: boolean;
  onDone: () => Promise<void>;
}) {
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The open items the owner has chosen to carry, by name (ADR-0069 A4).
  const [carrying, setCarrying] = useState<Record<string, ItemDraft>>({});
  const carried = brief.open.map(briefItemName).filter((n) => carrying[n] !== undefined);
  const unpriced = carried.filter((n) => !isPriced(carrying[n] ?? BLANK_ITEM));

  async function onRelease() {
    setBusy(true);
    setError(null);
    try {
      await approveCrp(
        code,
        brief.crp,
        rationale.trim() || null,
        carried.map((item) => {
          const d = carrying[item] ?? BLANK_ITEM;
          return {
            item,
            exposure: d.exposure.trim(),
            exposureAmount: dollars(d.amount),
            exposureCap: dollars(d.cap),
          };
        }),
        brief.open.map(briefItemName),
      );
      setRationale('');
      setCarrying({});
      await onDone();
    } catch (err) {
      if (err instanceof CrpConflictError) {
        setError(err.message);
        await onDone();
      } else if (err instanceof CrpForbiddenError || err instanceof AttestationInvalidError) {
        setError(err.message);
      } else setError(err instanceof Error ? err.message : 'Could not record the release.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card crp">
      <h3 className="card__title">Release spend — {brief.title}</h3>
      <div className="crp__cols">
        <CrpColumn
          title="Verified"
          tone="good"
          items={brief.verified}
          empty="Nothing verified yet."
        />
        <CrpColumn title="Open items" tone="warn" items={brief.open} empty="No open items." />
        <CrpColumn
          title="Priced exposure"
          tone="risk"
          items={brief.pricedExposure}
          empty="No priced exposure."
        />
      </div>
      <p className="crp__rec">
        <span className="crp__rec-label">Recommendation</span>
        {brief.recommendation}
      </p>
      {!prerequisiteMet && requires ? (
        <p className="card__note">
          Release {requires} first — spend is approved in order: pursuit, then diligence, then
          design.
        </p>
      ) : !lockedBasisMet ? (
        <p className="card__note">
          Lock the basis first — {brief.crp} approves the spend that everything after it is measured
          against.
        </p>
      ) : blocked ? (
        <p className="notice warn">
          <strong>Blocked by a Stop&amp;Fix.</strong> Resolve it in the Variances panel before
          releasing {brief.crp}.
        </p>
      ) : canApprove ? (
        <div className="crp__approve">
          {brief.open.length > 0 && (
            <>
              <p className="card__prose">
                Carry an open item only if you can name its priced downside. It opens a Flag on the
                Lead Owner with a 7-day clock. A client can read the downside you write, but not the
                price or cap. Anything you leave is recorded as not carried.
              </p>
              <ul className="crp__attest">
                {brief.open.map(briefItemName).map((name) => {
                  const d = carrying[name];
                  const set = (patch: Partial<ItemDraft>) =>
                    setCarrying((prev) => ({
                      ...prev,
                      [name]: { ...(prev[name] ?? BLANK_ITEM), ...patch },
                    }));
                  return (
                    <li key={name}>
                      <label>
                        <input
                          type="checkbox"
                          checked={d !== undefined}
                          onChange={(e) =>
                            setCarrying((prev) => {
                              if (e.target.checked) return { ...prev, [name]: { ...BLANK_ITEM } };
                              return Object.fromEntries(
                                Object.entries(prev).filter(([k]) => k !== name),
                              );
                            })
                          }
                          disabled={busy}
                        />{' '}
                        Carry {name}
                      </label>
                      {d !== undefined && (
                        <>
                          <input
                            type="text"
                            aria-label={`Downside — ${name}`}
                            placeholder="The downside of proceeding without it"
                            value={d.exposure}
                            onChange={(e) => set({ exposure: e.target.value })}
                            disabled={busy}
                          />
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`Price in dollars — ${name}`}
                            placeholder="Price, $"
                            value={d.amount}
                            onChange={(e) => set({ amount: e.target.value })}
                            disabled={busy}
                          />
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`Cap in dollars — ${name}`}
                            placeholder="Cap, $"
                            value={d.cap}
                            onChange={(e) => set({ cap: e.target.value })}
                            disabled={busy}
                          />
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
              {unpriced.length > 0 && (
                <p className="muted" role="status">
                  A carried item needs its downside, a price in dollars (like 40,000) and a cap at
                  or above the price: {unpriced.join(', ')}.
                </p>
              )}
            </>
          )}
          <label className="crp__label" htmlFor={`crp-rationale-${brief.crp}`}>
            Release rationale{' '}
            <span className="muted">(optional — defaults to the recommendation)</span>
          </label>
          <textarea
            id={`crp-rationale-${brief.crp}`}
            className="crp__textarea"
            rows={2}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Why you're approving this spend…"
            disabled={busy}
          />
          {error && <p className="notice error">{error}</p>}
          <button
            type="button"
            className="crp__release"
            onClick={() => void onRelease()}
            disabled={busy || unpriced.length > 0}
          >
            {busy ? 'Releasing…' : `Release ${brief.crp} spend`}
          </button>
        </div>
      ) : (
        <p className="card__note">Releasing spend is reserved to an owner or admin.</p>
      )}
    </section>
  );
}

const BLANK_ITEM: ItemDraft = { disposition: 'verified', exposure: '', amount: '', cap: '' };

/** One checklist line as the owner is attesting it. */
interface ItemDraft {
  disposition: 'verified' | 'carried';
  exposure: string;
  amount: string;
  cap: string;
}

/**
 * Dollars typed by a person, or null. Strict, because the figure lands on an append-only record:
 * "40.000" read as forty would stand for good, so a form this does not recognise is refused, not guessed.
 */
export function dollars(typed: string): number | null {
  const t = typed.trim();
  if (!/^\$?(\d+|\d{1,3}(,\d{3})+)(\.\d{1,2})?$/.test(t)) return null;
  return Number(t.replace(/[$,]/g, ''));
}

function isPriced(d: ItemDraft): boolean {
  const amount = dollars(d.amount);
  const cap = dollars(d.cap);
  return d.exposure.trim() !== '' && amount !== null && cap !== null && amount > 0 && cap >= amount;
}

/**
 * Advance the record by attesting its phase (ADR-0070 D3/D4). Every checklist item is either verified
 * or carried; a carried item names its priced downside and opens a live Flag on the Lead Owner. Offered
 * only once the spend for the phase being entered has been released.
 */
function AdvancePanel({
  code,
  offer,
  canApprove,
  blocked,
  onDone,
}: {
  code: string;
  offer: AdvanceOffer;
  canApprove: boolean;
  blocked: boolean;
  onDone: () => Promise<void>;
}) {
  const fresh = (): Record<string, ItemDraft> =>
    Object.fromEntries(offer.checklist.map((i) => [i.label, { ...BLANK_ITEM }]));
  const [items, setItems] = useState<Record<string, ItemDraft>>(fresh);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(fresh());
    setError(null);
    setRationale('');
  }, [offer.from]);

  const nothingVerified = offer.checklist.every((i) => items[i.label]?.disposition === 'carried');
  const unpriced = offer.checklist.filter((i) => {
    const d = items[i.label];
    return d?.disposition === 'carried' && !isPriced(d);
  });

  async function onAdvance() {
    setBusy(true);
    setError(null);
    try {
      await advanceProject(
        code,
        offer.from,
        offer.checklist.map((i) => {
          const d = items[i.label] ?? BLANK_ITEM;
          const carried = d.disposition === 'carried';
          return {
            label: i.label,
            disposition: d.disposition,
            exposure: carried ? d.exposure.trim() : null,
            exposureAmount: carried ? dollars(d.amount) : null,
            exposureCap: carried ? dollars(d.cap) : null,
          };
        }),
        rationale.trim() || null,
      );
      await onDone();
    } catch (err) {
      if (err instanceof CrpConflictError) {
        setError(err.message);
        await onDone();
      } else if (err instanceof CrpForbiddenError || err instanceof AttestationInvalidError) {
        setError(err.message);
      } else setError(err instanceof Error ? err.message : 'Could not record the advance.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card crp">
      <h3 className="card__title">
        Advance phase — {stateLabel(offer.from)} → {stateLabel(offer.to)}
      </h3>
      {!offer.spendReleased ? (
        <p className="card__note">
          Release {offer.requiredCrp} spend first. Entering {stateLabel(offer.to)} before its spend
          is approved would make the release retroactive.
        </p>
      ) : blocked ? (
        <p className="notice warn">
          <strong>Blocked by a Stop&amp;Fix.</strong> Resolve it in the Variances panel before
          advancing.
        </p>
      ) : !canApprove ? (
        <p className="card__note">Attesting a phase is reserved to an owner or admin.</p>
      ) : (
        <div className="crp__approve">
          <p className="card__prose">
            Attest each item. Carry one only if you can name its priced downside — it opens a Flag
            on the Lead Owner with a 7-day clock.
          </p>
          <ul className="crp__attest">
            {offer.checklist.map((i) => {
              const d = items[i.label] ?? BLANK_ITEM;
              return (
                <li key={i.label}>
                  <strong>{i.label}</strong> <span className="muted">{i.detail}</span>
                  <div>
                    {(['verified', 'carried'] as const).map((disp) => (
                      <label key={disp}>
                        <input
                          type="radio"
                          name={`attest-${i.label}`}
                          checked={d.disposition === disp}
                          onChange={() =>
                            setItems((prev) => ({
                              ...prev,
                              [i.label]: { ...d, disposition: disp },
                            }))
                          }
                          disabled={busy}
                        />{' '}
                        {disp === 'verified' ? 'Verified' : 'Carry open'}
                      </label>
                    ))}
                  </div>
                  {d.disposition === 'carried' && (
                    <>
                      <input
                        type="text"
                        aria-label={`Downside — ${i.label}`}
                        placeholder="The downside of proceeding without it"
                        value={d.exposure}
                        onChange={(e) =>
                          setItems((prev) => ({
                            ...prev,
                            [i.label]: { ...d, exposure: e.target.value },
                          }))
                        }
                        disabled={busy}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`Price in dollars — ${i.label}`}
                        placeholder="Price, $"
                        value={d.amount}
                        onChange={(e) =>
                          setItems((prev) => ({
                            ...prev,
                            [i.label]: { ...d, amount: e.target.value },
                          }))
                        }
                        disabled={busy}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`Cap in dollars — ${i.label}`}
                        placeholder="Cap, $"
                        value={d.cap}
                        onChange={(e) =>
                          setItems((prev) => ({
                            ...prev,
                            [i.label]: { ...d, cap: e.target.value },
                          }))
                        }
                        disabled={busy}
                      />
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          {unpriced.length > 0 && (
            <p className="muted" role="status">
              A carried item needs its downside, a price in dollars (like 40,000) and a cap at or
              above the price: {unpriced.map((i) => i.label).join(', ')}.
            </p>
          )}
          <label className="crp__label" htmlFor="advance-rationale">
            Rationale <span className="muted">(optional)</span>
          </label>
          <textarea
            id="advance-rationale"
            className="crp__textarea"
            rows={2}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            disabled={busy}
          />
          {nothingVerified && (
            <p className="notice warn">
              <strong>Nothing is verified.</strong> Every item is carried open. You can still
              advance, and the Decision Record will say so in those words.
            </p>
          )}
          {error && <p className="notice error">{error}</p>}
          <button
            type="button"
            className="crp__release"
            onClick={() => void onAdvance()}
            disabled={busy || unpriced.length > 0}
          >
            {busy ? 'Advancing…' : `Attest and advance to ${stateLabel(offer.to)}`}
          </button>
        </div>
      )}
    </section>
  );
}

/** A carried item's price and cap, when this reader was served them (staff only). */
export function carriedFigures(v: Pick<Variance, 'detail'>): string | null {
  const d = v.detail as { exposureAmount?: unknown; exposureCap?: unknown } | null | undefined;
  if (typeof d?.exposureAmount !== 'number' || typeof d.exposureCap !== 'number') return null;
  const usd = (n: number) => `$${n.toLocaleString('en-US')}`;
  return `Priced at ${usd(d.exposureAmount)}, capped at ${usd(d.exposureCap)}`;
}

/**
 * The locked basis (ADR-0071): the approved math that governs execution. Shows what governs and the
 * locks before it; an OWNER may lock or re-lock it with a reason. The api enforces the rest.
 */
function BasisPanel({
  code,
  basis,
  canLock,
  onDone,
}: {
  code: string;
  basis: BasisState | null;
  canLock: boolean;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const governing = basis?.governing ?? null;
  const current = basis?.current ?? null;

  async function onLock() {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      await lockBasis(code, reason.trim(), current.analysisId, governing?.id ?? null);
      setReason('');
      await onDone();
    } catch (err) {
      setError(err instanceof BasisLockError ? err.message : 'Could not lock the basis.');
      if (err instanceof BasisLockError) await onDone();
    } finally {
      setBusy(false);
    }
  }

  const money = (n: number | null | undefined) =>
    typeof n === 'number' ? `$${Math.round(n).toLocaleString('en-US')}` : '—';

  return (
    <section className="card">
      <h3 className="card__title">Locked basis</h3>
      {governing ? (
        <>
          <p className="card__prose">
            Governing since {timestamp(governing.lockedAt)}, locked by {governing.lockedByName}:{' '}
            {governing.reason}
          </p>
          <p className="card__note">
            NOI {money(governing.dealInputs.noi)} · Total cost{' '}
            {money(governing.dealInputs.totalCost)} · Loan {money(governing.dealInputs.loanAmount)}
            {basis && basis.chain.length > 1
              ? ` · ${basis.chain.length - 1} earlier lock(s) kept on the record`
              : ''}
          </p>
        </>
      ) : (
        <p className="card__prose">
          No basis is locked. Until one is, every comparison is against the last run, and CRP-3
          cannot be released.
        </p>
      )}
      {canLock && !current && (
        <p className="card__note">
          Nothing to lock — the latest run is not a completed underwrite.
        </p>
      )}
      {canLock && current ? (
        <div className="crp__approve">
          {/* The owner attests THESE numbers — the server refuses if a newer run lands first. */}
          <p className="card__note">
            {governing ? 'Re-locking would freeze' : 'Locking would freeze'}: NOI{' '}
            {money(current.dealInputs.noi)} · Total cost {money(current.dealInputs.totalCost)} ·
            Loan {money(current.dealInputs.loanAmount)}
          </p>
          <label className="crp__label" htmlFor="basis-reason">
            {governing
              ? 'Why the basis changed (required)'
              : 'Why this is the approved basis (required)'}
          </label>
          <textarea
            id="basis-reason"
            className="crp__textarea"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
          />
          {error && <p className="notice error">{error}</p>}
          <button
            type="button"
            className="crp__release"
            onClick={() => void onLock()}
            disabled={busy || reason.trim() === ''}
          >
            {busy ? 'Locking…' : governing ? 'Re-lock the basis' : 'Lock the basis'}
          </button>
        </div>
      ) : (
        !canLock && (
          <p className="card__note">Locking the basis is reserved to the project owner.</p>
        )
      )}
    </section>
  );
}

/** The release and advance actions open at the record's state (ADR-0070). */
function LifecyclePanels({
  code,
  crp,
  canApprove,
  blocked,
  onDone,
}: {
  code: string;
  crp: CrpState | null;
  canApprove: boolean;
  blocked: boolean;
  onDone: () => Promise<void>;
}) {
  if (!crp || (crp.releases.length === 0 && crp.advance === null)) {
    return (
      <section className="card">
        <h3 className="card__title">Releases and advances</h3>
        <p className="card__prose">
          Nothing to release or attest at this state. See the Decision Record timeline below.
        </p>
      </section>
    );
  }
  return (
    <>
      <p className="card__note">
        The record is {stateLabel(crp.state)}. Spend released so far:{' '}
        {crp.released.length > 0 ? crp.released.join(', ') : 'none yet'}.
      </p>
      {crp.releases.map((r) => (
        <ReleasePanel
          key={r.crp}
          code={code}
          brief={r.brief}
          requires={r.requires}
          prerequisiteMet={r.prerequisiteMet}
          lockedBasisMet={r.lockedBasisMet}
          canApprove={canApprove}
          blocked={blocked}
          onDone={onDone}
        />
      ))}
      {crp.advance && (
        <AdvancePanel
          code={code}
          offer={crp.advance}
          canApprove={canApprove}
          blocked={blocked}
          onDone={onDone}
        />
      )}
    </>
  );
}

function CrpColumn({
  title,
  tone,
  items,
  empty,
}: {
  title: string;
  tone: 'good' | 'warn' | 'risk';
  items: CrpBrief['verified'];
  empty: string;
}) {
  return (
    <div className={`crpcol crpcol--${tone}`}>
      <h4 className="crpcol__title">{title}</h4>
      {items.length === 0 ? (
        <p className="crpcol__empty muted">{empty}</p>
      ) : (
        <ul className="crpcol__list">
          {items.map((it, i) => (
            <li key={`${it.label}-${i}`} className="crpcol__item">
              <span className="crpcol__label">{it.label}</span>
              <span className="crpcol__detail">{it.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * What the Clear step says, per `clearEffect` (#1155). `unknown` — an older server sent none — makes no
 * promise, because each of the three readings is false for some row.
 */
const CLEAR_EFFECT_COPY: Record<NonNullable<Variance['clearEffect']> | 'unknown', string> = {
  retires:
    'Clearing retires this finding for good. If a later run finds this same move again — the same value, against the same reference, at the same rung — it is not raised again. Any other move is still raised.',
  retires_band:
    'Clearing retires this finding against the locked basis. A later run is not raised again while the deal stays off the basis in the same direction, at the same rung, under the same lock — unless the move grows by at least 5 points. A move the other way, a worse rung, or a new lock is raised.',
  ends_finding:
    'Clearing ends this stop. It is not re-opened from this finding: if the hazard returns, it has to be recorded as a new finding.',
  may_return:
    'Clearing closes this finding. The clear does not retire it, so a later run may raise it again.',
  unknown: 'Clearing closes this finding.',
};

/**
 * The Variance Response Ladder panel (ADR-0028 D6 / FR-M3-03) — what re-verification caught. An open
 * `stop_fix` is a hard block on advancement; a `flag` is surfaced for review. An owner/admin clears an
 * open variance (writing an immutable `variance_clearance` Decision Record); clearing an open Stop&Fix
 * unfreezes the CRP release. Renders only when the project has variances (a clean project shows nothing).
 */
export function Variances({
  code,
  variances,
  posture,
  canClear,
  onCleared,
}: {
  code: string;
  variances: Variance[];
  posture: VariancePosture;
  canClear: boolean;
  onCleared: () => Promise<void>;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
    THE CONFIRM STEP (#1155). A clear can retire a finding for good (ADR-0048 §9) or end one for good
    (ADR-0063 D3), so Clear no longer acts on one click. It opens a step that says what THIS clear will
    do and invites a reason. The reason is never required — canon's `said()` (Chad, 2026-07-14) — and
    neither side enforces one: a blank note is recorded as "no note given".
  */
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  // The write-off that has been asked for once. It cannot be undone, so it takes a second click.
  const [armedWriteOff, setArmedWriteOff] = useState<string | null>(null);

  /*
    ⚠️ ONE PRINT REF PER `<details>`, because a ref points at one element. A closed disclosure hides
    its content through the user agent's shadow slot, so a print stylesheet cannot reopen it — a
    record printed for an IC packet would lose its flags entirely. `usePrintExpanded.test.ts` enforces
    this and caught the first version of this panel.

    ⚠️ THESE STAY ABOVE THE EARLY RETURN (#1157). The parent mounts this panel whether or not the
    project has variances, so a project that gains its FIRST variance while open re-renders the same
    component with two more hooks than last time, and React throws "Rendered more hooks than during
    the previous render". Hook count must not depend on `variances`.
  */
  const flagsPrintRef = usePrintExpanded();
  const settledPrintRef = usePrintExpanded();

  if (variances.length === 0) return null;

  async function onClear(id: string, rationale: string, action: 'clear' | 'write-off' = 'clear') {
    setBusyId(id);
    setError(null);
    try {
      const trimmed = rationale.trim();
      const note = trimmed === '' ? null : trimmed;
      if (action === 'clear') await clearVariance(code, id, note);
      else await clearVariance(code, id, note, action);
      setConfirmId(null);
      setReason('');
      await onCleared(); // refresh — a cleared Stop&Fix unblocks the CRP panel.
    } catch (err) {
      if (err instanceof CrpForbiddenError || err instanceof CrpConflictError)
        setError(err.message);
      else setError(err instanceof Error ? err.message : 'Could not clear the variance.');
      await onCleared();
    } finally {
      setBusyId(null);
    }
  }

  const open = variances.filter((v) => v.status === 'open');
  const now = Date.now();

  /*
    Partitioned by CONSEQUENCE, not by count. `blocking` stops the record advancing and is always
    visible; `informational` is open but blocks nothing; `settled` is history, kept rather than
    dropped because a variance that was answered is part of the record.
  */
  const openRows = variances.filter((v) => v.clearedAt === null);
  const blocking = openRows.filter((v) => v.level === 'stop_fix');
  const informational = openRows.filter((v) => v.level !== 'stop_fix');
  const settled = variances.filter((v) => v.clearedAt !== null);

  /**
   * What a clear of THIS row will do, stated before it happens (#1155).
   *
   * ⚠️ The answer comes from the SERVER (`clearEffect`), because only the server knows. The workers
   * retire the exact move for a closed set of categories (`RESOLUTION_RETIRES`); a stop opened from a
   * confirmed finding is never re-opened from it (ADR-0063 D3). A predicate written here ("everything but
   * data completeness") promised a retirement the workers never keep, and its replacement promised a
   * return that never comes on a life-safety stop — both caught by red-team passes on #1156.
   *
   * ⚠️ THE REASON IS INVITED, NEVER REQUIRED — canon's ruling (Chad, 2026-07-14; `said()`), which canon
   * applies to resolving a Stop & Fix too. A blank one is recorded as "no note given", honestly.
   */
  const renderConfirm = (v: Variance) => {
    const effect = v.clearEffect;
    // Typed as possibly-undefined on purpose: a server that adds a FOURTH effect sends a value this
    // table has no copy for, and it must fall back to the no-promise line, not render an empty one.
    const carried = v.category === 'conditional_advance';
    const figures = carriedFigures(v);
    // A carried item never comes back from a run, so the effect copy would promise the wrong thing.
    const copy: string | undefined = carried
      ? 'Clear says this was verified. Write off says GGP accepts the downside. Neither can be undone.'
      : CLEAR_EFFECT_COPY[effect ?? 'unknown'];
    return (
      <div className="varclear" role="group" aria-label="Confirm clearing this variance">
        <p className="varclear__what">{copy ?? CLEAR_EFFECT_COPY.unknown}</p>
        <label className="varclear__label">
          Reason — optional. It is kept on the record; without one, the record says “no note given”.
          <textarea
            aria-description={carried ? 'A client can read this reason' : undefined}
            className="varclear__reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={4000}
            rows={2}
          />
        </label>
        {carried && <p className="muted">A client can read this reason.</p>}
        {error && <p className="notice error">{error}</p>}
        <div className="varclear__actions">
          <button
            type="button"
            className="varclear__cancel"
            onClick={() => {
              setConfirmId(null);
              setArmedWriteOff(null);
              setError(null);
            }}
            disabled={busyId !== null}
          >
            Cancel
          </button>
          <button
            type="button"
            className="varrow__clear"
            onClick={() => void onClear(v.id, reason)}
            disabled={busyId !== null}
          >
            {busyId === v.id
              ? 'Working…'
              : effect === 'retires' || effect === 'retires_band'
                ? 'Clear and retire'
                : 'Clear'}
          </button>
          {v.category === 'conditional_advance' && (
            <button
              type="button"
              className="varrow__clear"
              onClick={() => {
                if (armedWriteOff === v.id) void onClear(v.id, reason, 'write-off');
                else setArmedWriteOff(v.id);
              }}
              disabled={busyId !== null}
            >
              {armedWriteOff === v.id
                ? `Yes, write it off${figures ? ` (${figures.toLowerCase()})` : ''}`
                : 'Write off…'}
            </button>
          )}
        </div>
      </div>
    );
  };

  /** One row, rendered from three groups — a second copy would drift the moment either changed. */
  const renderRow = (v: Variance) => (
    <li
      key={v.id}
      className={`varrow varrow--${v.status}${confirmId === v.id ? ' varrow--confirming' : ''}`}
    >
      <span className={`varbadge varbadge--${v.level}`}>
        {v.level === 'stop_fix' ? 'Stop & Fix' : 'Flag'}
      </span>
      <div className="varrow__body">
        <span className="varrow__summary">{v.summary}</span>
        {carriedFigures(v) && <span className="varrow__meta muted">{carriedFigures(v)}</span>}
        <span className="varrow__meta muted">
          {v.category}
          {/*
                    HOW BIG the drift is — the quantity the ladder actually classifies on
                    (`classifyVariance` thresholds at 5% and 10%), and the one thing an owner deciding
                    whether to clear a release-blocking Stop&Fix could not see. It was served here from
                    the day the panel shipped and rendered nowhere.

                    ⚠️ A RATIO, not a percent, despite the name: `lifecycle.ts` computes it as
                    |current − prior| / |prior|. Rendered raw it prints "0%" for a 21.8% drift.

                    ⚠️ Rendered only when present, never as a dash. `percentFromRatio` returns "—" for
                    null, and a dash here would assert a drift that was measured and is unknown — a
                    variance can legitimately carry no magnitude (a categorical finding has none).

                    One decimal because the thresholds are 5% and 10%: "5%" and "5%" would render a
                    flag and a Stop&Fix identically at the boundary that separates them.

                    Absolute value: the classifier is sign-independent, and the DIRECTION is what
                    `summary` says in words. This line answers "by how much", not "which way".
                  */}
          {v.magnitudePct != null && Number.isFinite(v.magnitudePct) && (
            <> · {percentFromRatio(Math.abs(v.magnitudePct), 1)} drift</>
          )}{' '}
          · opened {timestamp(v.openedAt)}
          {/*
                    WHO OWNS THE REMEDIATION, and BY WHEN — canon GT §4.4's "named remediation owner and
                    a clock, assigned at the moment of trigger". Shown only while the variance is OPEN:
                    once it is cleared the meaningful actor is the clearer, rendered below, and a stale
                    deadline beside a closed finding reads as an outstanding obligation.

                    ⚠️ A MISSING OWNER IS STATED, NOT OMITTED. Rendering nothing would make an
                    unassigned Stop & Fix look identical to one somebody is working — the exact
                    "paralysis with a dashboard" the rule names.
                  */}
          {v.status === 'open' && (
            <>
              {' '}
              ·{' '}
              {v.ownerName ? (
                <>owner {v.ownerName}</>
              ) : (
                <span className="varrow__unowned">no remediation owner</span>
              )}
              {v.dueAt && Number.isFinite(new Date(v.dueAt).getTime()) && (
                <>
                  {' '}
                  ·{' '}
                  {new Date(v.dueAt).getTime() < now ? (
                    <span className="varrow__overdue">past due {timestamp(v.dueAt)}</span>
                  ) : (
                    <>due {timestamp(v.dueAt)}</>
                  )}
                </>
              )}
            </>
          )}
          {v.status === 'cleared' &&
            /* WHO cleared it, not just when. The id was served here all along and shown by
                       nothing; a governance stop that cannot name its clearer is not auditable. */
            ` · cleared ${timestamp(v.clearedAt)}${v.clearedByName ? ` by ${v.clearedByName}` : ''}`}
        </span>
      </div>
      {v.status === 'open' && canClear && confirmId !== v.id && (
        <button
          type="button"
          className="varrow__clear"
          onClick={() => {
            setConfirmId(v.id);
            setArmedWriteOff(null);
            setReason('');
            setError(null);
          }}
          disabled={busyId !== null}
        >
          Clear…
        </button>
      )}
      {v.status === 'open' && canClear && confirmId === v.id && renderConfirm(v)}
      {v.status === 'cleared' && (
        <span className="varrow__cleared muted">
          {v.closedAs === 'written_off' ? 'Written off' : 'Cleared'}
        </span>
      )}
    </li>
  );

  return (
    <section className="card variances">
      <h3 className="card__title">
        Variances
        {open.length > 0 && <span className="variances__count">{open.length} open</span>}
      </h3>
      {/*
        THE POSTURE — the severest OPEN rung, and whether anyone is actually on it. Chad's engine
        recomputes this after every ingest; it is what turns "how is this project doing?" into one
        answer instead of a list a reader has to scan.

        ⚠️ `unowned` and `overdue` are shown BESIDE the rung, never folded into it. Canon GT §4.4:
        "a stop without an owner and a deadline is not discipline; it is paralysis with a dashboard" —
        a Stop & Fix nobody owns and a Stop & Fix assigned this morning are the same rung and a
        completely different position, and a reader who cannot tell them apart has the dashboard.
      */}
      {posture.rung !== 'clear' && (
        <p className={`posture posture--${posture.rung}`}>
          <span className={`varbadge varbadge--${posture.rung}`}>
            {posture.rung === 'stop_fix' ? 'Stop & Fix' : 'Flag'}
          </span>{' '}
          <strong>Posture:</strong> {posture.open} open
          {posture.openStopFix > 0 && <> · {posture.openStopFix} blocking advancement</>}
          {posture.overdue > 0 && (
            <>
              {' '}
              · <span className="posture__bad">{posture.overdue} past due</span>
            </>
          )}
          {posture.unowned > 0 && (
            <>
              {' '}
              · <span className="posture__bad">{posture.unowned} with no remediation owner</span>
            </>
          )}
          {/* Only rows written before migration 0038 can be unclocked. Named rather than hidden —
              "no deadline recorded" is a different fact from "not yet due", and silently merging
              them is the collapse the resolution layer refuses. */}
          {posture.unclocked > 0 && <> · {posture.unclocked} with no deadline recorded</>}
        </p>
      )}
      {/* While a confirm step is ON SCREEN its error renders inside it — a Flag's step sits inside a
          collapsed list, possibly twenty rows below this line, where a message up here is unseen.
          ⚠️ "On screen", not "confirmId is set": after a 409 the refresh returns the row CLEARED, its
          step stops rendering, and keying on confirmId alone showed "already cleared" nowhere (third
          red-team pass on #1156). A 404 — the row gone — is the same shape. */}
      {error && !variances.some((v) => v.id === confirmId && v.status === 'open') && (
        <p className="notice error">{error}</p>
      )}
      {/*
        ── WHAT BLOCKS IS OPEN; WHAT IS INFORMATIONAL COLLAPSES ────────────────────────────────────

        🔴 MEASURED, NOT GUESSED. The Commons carries 27 open variances — 6 Stop & Fix and 21 Flags —
        beside 21 register findings, so the Record tab rendered ~48 multi-line rows in one panel and
        Brad could not work it. The row detail already collapses (a previous session did that); what
        was missing was a GROUP level.

        The split is by consequence, not by count. A Stop & Fix hard-blocks the record from advancing,
        so it is never behind a disclosure — hiding the thing that stops the project is the one
        arrangement worse than a long list. A Flag is a finding to read, and 21 of them are reference
        material until someone chooses to read them.

        ⚠️ NOTHING IS HIDDEN WITHOUT A SIGNAL — the same rule as the tab badges. Each group's summary
        carries its count and what it means, so a reader can see there are 21 flags without opening
        them and cannot mistake the panel for empty.

        `<details>` rather than React state: it works with find-in-page, it is keyboard-navigable and
        screen-reader-correct for free, and it prints expandable — which a governed record has to be.
      */}
      {blocking.length > 0 && <ul className="varlist">{blocking.map(renderRow)}</ul>}
      {informational.length > 0 && (
        <details className="worklist" open={blocking.length === 0} ref={flagsPrintRef}>
          <summary>
            {informational.length} flag{informational.length === 1 ? '' : 's'} to read. Nothing is
            blocked by these.
          </summary>
          <ul className="varlist">{informational.map(renderRow)}</ul>
        </details>
      )}
      {settled.length > 0 && (
        <details className="worklist" ref={settledPrintRef}>
          <summary>
            {settled.length} cleared. Kept, because a variance that was answered is part of the
            record.
          </summary>
          <ul className="varlist">{settled.map(renderRow)}</ul>
        </details>
      )}
    </section>
  );
}

/**
 * The immutable Decision Record timeline — the governance audit trail (newest first). Every row can be
 * disputed (ADR-0076); the dispute action sits on the row it is about.
 */
function Timeline({
  decisions,
  disputes,
}: {
  decisions: DecisionRecord[];
  disputes: DisputesContext;
}) {
  return (
    <section className="card timeline">
      <h3 className="card__title">Decision record</h3>
      {decisions.length === 0 ? (
        <p className="card__prose">
          No governance events yet. Releases of spend and advances will appear here as an immutable,
          append-only trail.
        </p>
      ) : (
        <ol className="tl">
          {decisions.map((d) => (
            <li key={d.id} className={`tl__row tl__row--${d.kind}`}>
              <span className="tl__dot" aria-hidden="true" />
              <div className="tl__body">
                <span className="tl__headline">{decisionHeadline(d)}</span>
                {d.rationale && <span className="tl__rationale">{d.rationale}</span>}
                {(attestationOf(d) ?? releaseCarryOf(d))?.carried.map((c) => (
                  <span key={c.item} className="tl__rationale">
                    Carried open: {c.item} — {c.exposure}
                    {typeof c.exposureAmount === 'number' && typeof c.exposureCap === 'number'
                      ? ` (priced at $${c.exposureAmount.toLocaleString('en-US')}, capped at $${c.exposureCap.toLocaleString('en-US')})`
                      : ''}
                  </span>
                ))}
                {(releaseCarryOf(d)?.notCarried.length ?? 0) > 0 && (
                  <span className="tl__rationale">
                    Not carried: {releaseCarryOf(d)?.notCarried.join(', ')}
                  </span>
                )}
                <span className="tl__stamp muted">
                  {/* ⚠️ "the Engine" is the TRUTH for a system-initiated transition, not a
                      placeholder — a connector-synced re-analysis has no human. Inventing one
                      would be the confidently-wrong value this column exists to avoid. */}
                  {deciderLabel(d.actorName, d.actorUserId)} · {timestamp(d.decidedAt)}
                </span>
                <DisputeAction ctx={disputes} decision={d} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** A one-line description of a Decision Record. Exported for unit tests (the branching is the risk). */
export function decisionHeadline(d: DecisionRecord): string {
  if (d.kind === 'crp_approval') {
    // NEUTRAL on purpose (ADR-0070 D7): approvals made before ADR-0070 moved the record; approvals after
    // it approve spend. "released" is true of both, and history is not re-described.
    return `${d.crp ?? 'CRP'} released`;
  }
  if (d.kind === 'basis_lock') {
    // ADR-0071 D2 — the rationale says whether it was a first lock or a re-lock, and why.
    return 'Basis locked';
  }
  if (d.kind === 'variance_clearance') {
    return d.writtenOff === true ? 'Carried item written off' : 'Variance cleared';
  }
  if (d.kind === 'stop_attestation') {
    // ADR-0063 D3 — an owner confirmed (a Stop & Fix opened) or rejected a protected candidate. The
    // rationale line leads with which; without this branch the row fell through to "State
    // transition", which misdescribes a life-safety decision on the governance trail.
    return 'Protected-stop decision';
  }
  if (d.kind === 'promotion') {
    // ADR-0035 D2 / canon R-e — the IC approval that made a pursuit a GGP project and minted its
    // number. It carries no from/to state, so it used to fall through to "State transition": the
    // trail's most consequential human decision, described as bookkeeping.
    return 'Promoted to a GGP project';
  }
  // `intake` deliberately falls through: it carries `toState` (where the record began) and no
  // `fromState`, so it reads "Entered …" — the self-contained first row its planner designed.
  if (d.fromState && d.toState) {
    const a = attestationOf(d);
    const verb = a
      ? a.nothingVerified
        ? 'Advanced with nothing verified'
        : 'Attested and advanced'
      : 'Advanced';
    return `${verb} ${stateLabel(d.fromState)} → ${stateLabel(d.toState)}`;
  }
  if (d.toState) return `Entered ${stateLabel(d.toState)}`;
  return 'State transition';
}

/**
 * ── THE GATE'S QUESTIONS — C1 identity · C3 staleness · D6 self-consistency ──────────────────────
 *
 * 🔴 THIS CARD IS THE GATE'S FIRST READER. The checks have run on every analysis since tranche B
 * shipped, and their findings went into `gateFindings` on the insight published to Nexus — a key
 * Nexus's Deal Room has no field for, and one we never persisted on our side either. So an entire
 * shipped gate tranche was computed, logged, and seen by nobody.
 *
 * Brad ruled 2026-08-27 that the reader is ours: canon makes the PEE Gate a STAFF surface (ADR-0035),
 * and "confirm this is the right record" is a staff question, not a client one — so the client-facing
 * Deal Room was arguably never the right target.
 *
 * ## ⚠️ THREE STATES, AND RENDERING TWO OF THEM ALIKE IS THE DEFECT
 *
 * - `[…]` — questions to show.
 * - `[]` — the gate looked and found nothing. A stated negative, and worth saying out loud: it is the
 *   reader's grounds for trusting the record MORE.
 * - `null` — **it could not look.** Absent inputs, or a run completed before the gate was persisted.
 *   Rendering this as "no issues" would assert a clean nobody established, which is the whole failure
 *   the three-state contract exists to prevent. It is said plainly instead.
 *
 * ⚠️ The card is not hidden when empty, deliberately. A gate that only appears when it has complaints
 * cannot tell a reader the difference between "checked, clean" and "never ran" — and that difference
 * is the entire value of a verification surface.
 */
export function GateFindings({
  findings,
  code,
  onAnswered,
}: {
  findings: GateFinding[] | null | undefined;
  /** Present when the reader may answer (the staff read serves `responses`); absent in previews. */
  code?: string;
  onAnswered?: () => Promise<void> | void;
}) {
  // `undefined` (an older client's shape) and `null` (could not look) are the same fact to a reader:
  // nothing was recorded, so nothing can be claimed.
  const couldNotLook = findings === null || findings === undefined;

  return (
    <section className="card gate">
      <h3 className="card__title">
        Document checks
        {!couldNotLook && findings.length > 0 && (
          <span className="gate__count">
            {findings.length} {findings.length === 1 ? 'question' : 'questions'}
          </span>
        )}
      </h3>

      {couldNotLook && (
        <p className="muted">
          <strong>Not checked.</strong> These checks did not run on this underwrite — either the
          documents carried nothing to check, or the record predates the checks being recorded. This
          is not a clean result; nothing was examined.
        </p>
      )}

      {!couldNotLook && findings.length === 0 && (
        <p className="muted">
          Checked, and nothing to raise — the documents match this record, none are past the
          one-year threshold, and none disagree with themselves.
        </p>
      )}

      {!couldNotLook && findings.length > 0 && (
        <>
          {/*
            ⚠️ Says outright that nothing was changed or blocked. Canon: "prepared for your review,
            not decided." A reader who thinks the underwrite was held up by this will go looking for
            a release that never existed.
          */}
          <p className="muted gate__preamble">
            The underwrite ran on these documents as filed. Nothing below changed a number or
            blocked anything — each is a question for a person.
          </p>
          <ul className="varlist">
            {findings.map((f) => (
              <li key={`${f.check}-${f.specBlock}-${f.summary}`} className="varrow">
                <span className="gatebadge">{checkLabel(f.check)}</span>
                <div className="varrow__body">
                  <span className="varrow__summary">{f.summary}</span>
                  <span className="varrow__meta muted">{f.resolution}</span>
                  {f.answer ? (
                    <GateAnswerLine answer={f.answer} />
                  ) : f.responses && f.findingKey && code ? (
                    <GateAnswerForm
                      code={code}
                      findingKey={f.findingKey}
                      responses={f.responses}
                      onAnswered={onAnswered}
                    />
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** A question's answer, as the record holds it: what was chosen, why, and who said so. */
function GateAnswerLine({ answer }: { answer: GateAnswer }) {
  return (
    <p className="gate__answer">
      <strong>{answer.sentence}.</strong> {answer.reasoning}{' '}
      <span className="muted">
        — {answer.answeredByName}, {new Date(answer.answeredAt).toLocaleDateString()}
      </span>
    </p>
  );
}

/** The answers that say a document is coming. The answer records the saying; the upload files it. */
const FILING_ANSWERS = new Set(['contradoc', 'threshdoc', 'doc']);

/**
 * ADR-0069 N12 — answering a gate question with one of its own kind's answers (the response matrix).
 *
 * ⚠️ NOTHING IS PRE-SELECTED (Chad, July 14, 2026: "the machine prepares, a person decides; a default
 * answer is the machine deciding"). The note is required, as the route and the database require it.
 */
function GateAnswerForm({
  code,
  findingKey,
  responses,
  onAnswered,
}: {
  code: string;
  findingKey: string;
  responses: { id: string; sentence: string }[];
  onAnswered?: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [response, setResponse] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await answerGateQuestion(code, { findingKey, response, reasoning });
      await onAnswered?.();
    } catch (e) {
      // Kept on screen, not reloaded away: a refusal the reader cannot see is a refusal they retry.
      // If someone else answered first, the server says so in these words.
      setError(e instanceof Error ? e.message : 'We couldn’t record that answer just now.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="chal__send gate__answer-open" onClick={() => setOpen(true)}>
        Answer this question
      </button>
    );
  }
  return (
    <div className="chal__form chal__form--answer">
      <label className="chal__label" htmlFor={`gate-response-${findingKey}`}>
        Your response
      </label>
      <select
        id={`gate-response-${findingKey}`}
        value={response}
        onChange={(e) => setResponse(e.target.value)}
      >
        <option value="">Choose a response…</option>
        {responses.map((r) => (
          <option key={r.id} value={r.id}>
            {r.sentence}
          </option>
        ))}
      </select>
      <label className="chal__label" htmlFor={`gate-reason-${findingKey}`}>
        Why? (required)
      </label>
      <textarea
        id={`gate-reason-${findingKey}`}
        className="chal__textarea"
        rows={3}
        value={reasoning}
        maxLength={4000}
        onChange={(e) => setReasoning(e.target.value)}
      />
      <p className="chal__hint">Your answer goes on the record, with your name.</p>
      {FILING_ANSWERS.has(response) && (
        <p className="chal__hint">
          Recording this does not file the document. Upload it to the record as well.
        </p>
      )}
      {error && <p className="chal__error">{error}</p>}
      <div className="chal__actions">
        <button
          type="button"
          className="chal__send"
          disabled={busy || response === '' || reasoning.trim() === ''}
          onClick={() => void send()}
        >
          {busy ? 'Recording…' : 'Record the answer'}
        </button>
      </div>
    </div>
  );
}

/**
 * The check's name in a reader's words.
 *
 * ⚠️ Falls back to the raw key rather than to a generic word. A check added on the worker side and
 * not listed here should read as an unfamiliar NAME — which prompts someone to add it — rather than
 * silently flattening into "Check" and becoming invisible.
 */
/**
 * THE PROJECT PAGE STOPS BEING ONE LONG SCROLL.
 *
 * ## The problem
 *
 * A loaded project rendered TEN stacked cards — header, ladder, sponsor, facts, gate, variances, CRP,
 * documents, retrieval log, timeline. Read top to bottom that is several screens before a reader
 * reaches the thing they opened the project to see, and there was no way to send anyone to one part of
 * it. Brad raised it more than once; this is the fix.
 *
 * ## Why this reuses the Deal Room's shell rather than inventing one
 *
 * `ReportSections` already solves this exact problem, and its docstring states the same diagnosis for
 * the analyzer's report. Reusing it means the Hub and the Deal Room behave the same way — the same
 * keyboard contract, the same deep links, the same print behaviour — and a fix to one is a fix to both.
 * A second tab implementation would be a second set of accessibility bugs.
 *
 * Three properties carry over and all three matter here:
 *
 *   • **Every panel stays in the DOM**, hidden rather than unmounted — so browser find-in-page still
 *     searches the whole record, and switching tabs re-fetches nothing.
 *   • **Print expands everything.** A project that printed as one tab would be a broken export of a
 *     governed record.
 *   • **Tabs are deep-linkable** via the hash, so "look at the variances" is a link.
 *
 * ## 🔴 BADGES ARE NOT DECORATION — they are what makes tabbing honest
 *
 * Tabs hide things. A reader who cannot see that there are three open variances behind a tab is worse
 * off than one who had to scroll past them. So every tab carries the count of the thing a person would
 * have come looking for, and the grouping is by QUESTION rather than by component:
 *
 *   Overview   — who is behind this, and what did the gate ask about the documents?
 *   Record     — what does the record claim, and what has been flagged about it?
 *   Documents  — what came in, and what went out?
 *   Decisions  — what has been approved, and what happened when?
 */
/**
 * What each tab advertises.
 *
 * 🔴 EXPORTED SO IT IS TESTABLE, because this is the number that can lie. Tabs hide things; a badge is
 * the only thing telling a reader there are three variances behind one. Get the count wrong and the
 * tabbing is worse than the scroll it replaced.
 *
 * ⚠️ OPEN VARIANCES ONLY. A cleared variance is history — it belongs in the panel, not in a number
 * that says "look here". A badge counting every variance ever raised sends a reader to a tab where
 * nothing needs them, which teaches them to ignore the badge, and then it protects nobody.
 */
export function tabBadges(load: Extract<Load, { status: 'ready' }>): {
  gateQuestions: number;
  openVariances: number;
  documents: number;
  openDisputes: number;
  // OPEN settle-first design-review findings only: a settled one no longer holds up the price.
  settleFirst: number;
} {
  return {
    gateQuestions: load.project.gateFindings?.length ?? 0,
    openVariances: load.variances.filter((v) => v.clearedAt === null).length,
    documents: load.documents.length,
    // OPEN disputes only, on the same reasoning as variances: an answered dispute is history. An
    // unreadable ledger counts 0 here and says so in its own card.
    openDisputes: load.disputes?.openCount ?? 0,
    settleFirst: designReviewReport(load.facts, load.findingsWithheld).counts.settleFirst,
  };
}

function ProjectTabs({
  code,
  load,
  canIngest,
  canRetrieve,
  canAsk,
  canApprove,
  canAttest,
  canLock,
  canReview,
  canRerun,
  canReport,
  canDispute,
  canAnswerDisputes,
  userId,
  reload,
}: {
  code: string;
  load: Extract<Load, { status: 'ready' }>;
  canIngest: boolean;
  canRetrieve: boolean;
  canAsk: boolean;
  canApprove: boolean;
  canAttest: boolean;
  /** Owner-only: may lock or re-lock the basis (ADR-0071). */
  canLock: boolean;
  canReview: boolean;
  /** Staff on the Hub: may start a paid re-underwrite from the Documents panel. */
  canRerun: boolean;
  /** Staff on the Hub: may ask Nexus to render the deal report from the Documents panel. */
  canReport: boolean;
  /** Staff: may dispute a decision (ADR-0076). */
  canDispute: boolean;
  /** Owner/admin: may answer a dispute someone else filed. */
  canAnswerDisputes: boolean;
  /** The signed-in user, to withhold the answer form from a dispute's own filer. */
  userId: string | null;
  reload: () => Promise<void>;
}) {
  const { gateQuestions, openVariances, documents, openDisputes, settleFirst } = tabBadges(load);
  const disputes: DisputesContext = {
    code,
    ledger: load.disputes,
    canFile: canDispute,
    canAnswer: canAnswerDisputes,
    userId,
    documents: load.documents,
    decisions: load.decisions,
    describe: decisionHeadline,
    reload,
  };
  /*
    ⚠️ THE OTHER HALF OF THE SHARE VERB. `documentShareUrl` emits
    `/projects/:ref?doc=<id>#documents` — the hash opens this tab (ReportSections) and `?doc=` names
    WHICH document. Without it a shared link lands a colleague on a list of fifteen files and asks
    them to find the one you meant, which is slower than the drive, and R9 calls a verb that loses to
    the old way a defect.

    Read HERE rather than passed down: this component owns the documents section, and the value is
    null on every ordinary visit, so it costs nothing when nobody shared.
  */
  const [searchParams] = useSearchParams();
  const highlightDocumentId = searchParams.get('doc');

  const sections = buildSections([
    {
      id: 'overview',
      label: 'Overview',
      ...(gateQuestions > 0 ? { badge: gateQuestions } : {}),
      content: (
        <>
          <SponsorProfile
            code={code}
            sponsorName={load.project.sponsorName}
            sponsorContext={load.project.sponsorContext}
            canEdit={canIngest}
            onSaved={reload}
          />
          {/* What the GATE asked about the documents underneath all of it (tranche B). It leads the
              overview because a question about whether we are reading the right, current,
              self-consistent documents comes before anything derived from them. */}
          <GateFindings findings={load.project.gateFindings} code={code} onAnswered={reload} />
          {/* ADR-0077 — what moved since each schedule's or estimate's last version. Its questions are in
              the gate list above; this is every movement, with them. */}
          {load.project.versionDeltas && load.project.versionDeltas.length > 0 && (
            <VersionDeltasPanel deltas={load.project.versionDeltas} />
          )}
          {/* ADR-0077 D8 — where two of the record's documents state one thing differently. */}
          {load.project.crossDocument && (
            <VersionDeltasPanel
              title="Where the documents disagree"
              deltas={[load.project.crossDocument]}
            />
          )}
          {load.project.loanTie && (
            <VersionDeltasPanel
              title="The proforma's construction loan"
              deltas={[load.project.loanTie]}
            />
          )}
          {load.project.opportunityZone && (
            <VersionDeltasPanel
              title="Opportunity Zone check"
              deltas={[load.project.opportunityZone]}
            />
          )}
        </>
      ),
    },
    {
      id: 'record',
      label: 'Record',
      ...(openVariances > 0 ? { badge: openVariances } : {}),
      content: (
        <>
          {/* What the record CLAIMS, and how strongly (ADR-0044). Above the variances because a claim
              is not yet a finding: a reader should see what is asserted and how strongly before they
              see what has been flagged about it. */}
          <RecordFacts
            facts={load.facts}
            resolution={load.factResolution}
            openItems={load.openItems}
            reviewCoverage={load.reviewCoverage}
            findingsWithheld={load.findingsWithheld}
            canAttest={canAttest}
            onAttest={async (fact, outcome, rationale) => {
              const r = await attestFinding(code, fact.key, {
                outcome,
                factId: fact.id,
                rationale,
              });
              // A decision changes the finding AND may open a stop — re-read both.
              if (r.kind === 'ok') await reload();
              return r;
            }}
          />
          {/* ADR-0069 A8 — the floor the post-CRP-3 re-run tests; the owner may make it stricter. */}
          <YieldFloor
            facts={load.facts}
            canSet={canLock}
            onSet={async (floor, reason) => {
              const r = await setProjectYieldFloor(code, floor, reason);
              if (r.ok) await reload();
              return r;
            }}
          />
          <RecordReviews code={code} canRun={canReview} />
          <Variances
            code={code}
            variances={load.variances}
            posture={load.posture}
            canClear={canApprove}
            onCleared={reload}
          />
        </>
      ),
    },
    {
      /*
        A3 S9 — the design review as canon's report reads: the short version, settle first, then by
        discipline, each finding once. Every role sees it; the API decides what each reader was sent.
        The badge is the open settle-first count, the thing a reader opens the tab for.
      */
      id: 'design-review',
      label: 'Design review',
      when: hasDesignReview(load.facts, load.findingsWithheld),
      ...(settleFirst > 0 ? { badge: settleFirst } : {}),
      content: <DesignReviewReport facts={load.facts} withheld={load.findingsWithheld} />,
    },
    {
      /*
        The Open Items Register (ADR-0073). Its own tab because it is its own document: the facts panel
        shows only its counts, since ~150 items there would bury the restrictions and design findings.
        No badge — the count lives in the panel, and a second place for a number is a second place for
        it to be wrong.
      */
      id: 'open-items',
      label: 'Open items',
      /*
        STAFF ONLY. The register is GGP's internal working list — its questions, positions and the third
        parties on the hook — and the first client is a read-only viewer. The API already lets a viewer
        read it (`GET /facts` needs only a session), but offering a client the full text is a product
        call, not a side effect of building the tab; until it is made, the tab follows the Drawings rule.
      */
      when: canRetrieve,
      content: <OpenItemsRegister code={code} />,
    },
    {
      id: 'documents',
      label: 'Documents',
      ...(documents > 0 ? { badge: documents } : {}),
      content: (
        <>
          <ProjectDocuments
            code={code}
            highlightDocumentId={highlightDocumentId}
            canRetrieve={canRetrieve}
            documents={load.documents}
            /* What the RECORD believes it holds, which is not always what the gate can list — see the
               note in ProjectDocuments. Both numbers are true; the panel has to say so. */
            recordDocumentCount={load.project.documentCount}
            /* What the re-underwrite will actually judge — the server's count, never an estimate. */
            judgedDocumentCount={load.project.judgedDocumentCount ?? null}
            retrievals={load.retrievals.retrievals}
            canIngest={canIngest}
            canReunderwrite={canRerun}
            canRequestReport={canReport}
            onIngested={reload}
          />
          {/* What came IN is above; what went OUT is here. Together they are the record's full
              traffic, which is what canon R4 has to be able to answer before the drive can lock. They
              stay in ONE tab for that reason — splitting them would make the question unanswerable
              without switching panels. */}
          <RetrievalLog
            retrievals={load.retrievals.retrievals}
            truncated={load.retrievals.truncated}
          />
        </>
      ),
    },
    {
      /*
        The DRAWING record (ADR-0061). Its own tab rather than a block inside Documents, because they
        answer different questions: Documents is what the record HOLDS and who took it; this is what
        the current SET is. A 250-sheet table inside the documents panel would also bury the retrieval
        ledger that canon R4 depends on.

        No badge: the count is the whole content of the panel, and a badge repeating it would be a
        second place for the same number to be wrong.
      */
      id: 'drawings',
      label: 'Drawings',
      /*
        🔴 NOT OFFERED TO A VIEWER, matching the route's own gate. `GET /drawings/current` refuses
        without `retrieval:read` — the drawing record is staff-only (ADR-0061 D8). Offering the tab
        anyway put a client one click from "could not be loaded" on a contract exhibit, which reads as
        an OUTAGE rather than a boundary. Found by Bugbot on #1059.

        `when` rather than a conditional spread: that is how `buildSections` already filters, and a
        second mechanism for the same idea is one more thing to keep in step.
      */
      when: canRetrieve,
      content: (
        <>
          <ProjectDrawings code={code} />
          <DrawingSetAsFiled code={code} />
          {/* A3 S8: the register's tracked quantities, checked against every priced issue. */}
          <TrackedQuantities code={code} />
        </>
      ),
    },
    {
      id: 'decisions',
      label: 'Decisions',
      ...(openDisputes > 0 ? { badge: openDisputes } : {}),
      content: (
        <>
          <BasisPanel code={code} basis={load.basis} canLock={canLock} onDone={reload} />
          <LifecyclePanels
            code={code}
            crp={load.crp}
            canApprove={canApprove}
            blocked={hasOpenStopFix(load.variances)}
            onDone={reload}
          />
          <Timeline decisions={load.decisions} disputes={disputes} />
          <DisputeLedgerCard ctx={disputes} />
        </>
      ),
    },
    {
      /*
        ASK THE RECORD (ADR-0045). Last, because it reads across everything the tabs before it show —
        the record, the variances, the documents — and answers from them.

        🔴 NOT OFFERED TO A VIEWER, matching the route's own gate (`model:spend` + `retrieval:read`),
        for the reason the Drawings tab gives: a tab that can only ever answer 403 reads as a broken
        product, not a boundary.

        ⚠️ The panel sends nothing on mount. Tabs are hidden, not unmounted, so a mount-time call would
        spend a model call on every visit to this page.
      */
      id: 'ask',
      label: 'Ask',
      when: canAsk,
      // Keyed by the record, so a question and its answer can never carry over to another project.
      content: <ProjectAsk key={code} code={code} />,
    },
  ]);

  return <ReportSections sections={sections} />;
}

function checkLabel(check: string): string {
  if (check === 'identity') return 'Identity';
  if (check === 'staleness') return 'Out of date';
  if (check === 'self-consistency') return 'Contradiction';
  if (check === 'document-type') return 'Type mismatch';
  if (check === 'version-delta') return 'Changed since last version';
  if (check === 'cross-document') return 'Documents disagree';
  return check;
}
